import React, {
	type ReactNode,
	type ReactElement,
	type FC,
	useCallback,
	memo,
	// eslint-disable-next-line jira/restricted/react-suspense
	Suspense,
} from 'react';
import { useOwnershipMarkerComponent } from '@jira/platform__ownership-marker/src/ui/useOwnershipMarkerComponent.tsx';
import { getIsHydrationInProgress } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import type { LazySuspenseProps } from '@atlassian/react-loosely-lazy/suspense/types';
import { useHydrationSubtreeCompleter } from './useHydrationSubtreeCompleter.tsx';
import { useSwitchControl } from './useSwitchControl.tsx';
import { useHold } from './useHold.tsx';

const SuspenseWrapper: FC<{
	fallback: ReactNode;
	children: ReactNode;
	childrenRender: ReactElement;
	fallbackRender: ReactElement;
	unlocked?: boolean;
}> = ({ fallback, fallbackRender, children, childrenRender }) => (
	<Suspense
		fallback={
			<>
				{fallback}
				{fallbackRender}
			</>
		}
	>
		{children}
		{childrenRender}
	</Suspense>
);

/**
 * intermediate memo to avoid LazySuspense re-rendering when locked
 * also replaces useMemo for children and fallback "componsition"
 */
const MemoizedSuspense = memo(SuspenseWrapper, (prev, next) => {
	if (!next.unlocked) {
		return true;
	}

	return next.fallback === prev.fallback && next.children === prev.children;
});

/**
 * This is a "safe-to-use" Placeholder but it's actually expected to be used only around React-Loosely-Lazy components.
 * WARNING: Make sure we use same placeholders in a rendering tree.
 * Mixed use may cause unexpected behavior, such as rendering child component twice.
 * Ref: https://hello.atlassian.net/wiki/x/iJpOHwE
 *
 * @deprecated - do not use file directly (yet)
 */
export const SuspensePlaceholder: FC<{
	/**
	 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
	 * */
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}> = ({ name, children, fallback }) => {
	const [renderedPath, renderedPathRef, render] = useSwitchControl(name);

	const isHoldEnabled = useCallback(
		() => renderedPathRef.current !== 'children',
		[renderedPathRef],
	);

	useHold(
		name,
		// hold is "controlled" by the fact of children render
		renderedPath !== 'children',
		// if children become visible we don't need to hold anymore
		isHoldEnabled,
	);
	const isContentRendered = Boolean(renderedPath);
	useHydrationSubtreeCompleter({ isSubtreeRendered: isContentRendered, name });

	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });
	return (
		<OwnershipMarker>
			<MemoizedSuspense
				fallback={fallback}
				fallbackRender={render.fallback}
				childrenRender={render.children}
				unlocked={isContentRendered || !getIsHydrationInProgress()}
			>
				{children}
			</MemoizedSuspense>
		</OwnershipMarker>
	);
};
